import { uniq } from "lodash";
import { computeOffset, computeHeading } from 'spherical-geometry-js';
import chroma from "chroma-js";

export const convertPointToLatLng = (point) => {
  // Ensure that 'point' is a string before calling match
  if (typeof point !== 'string') {
    console.error('Invalid point value:', point);
    return { lat: null, lng: null };
  }

  // Extract coordinates from "POINT(lng lat)"
  const match = point.match(/POINT\(\s*(-?\d+\.\d+)\s+(-?\d+\.\d+)\s*\)/);

  if (match) {
    return {
      lng: parseFloat(match[1]), // Longitude
      lat: parseFloat(match[2])  // Latitude
    };
  } else {
    console.error('Invalid POINT format:', point);
    return { lat: null, lng: null }; // Handle invalid format gracefully
  }
}

export const wellColor = (well) => {
    let color = "#CCCCCC";
    if (!well.jobType || well.jobType === undefined) {
        well.jobType = "UNKNOWN";
    }
    color = well.jobType.includes("TREATMENT")
        ? "green"
        : well.jobType.includes("OFFSET")
        ? "blue"
        : well.jobType.includes("DFIT")
        ? "#FF0000"
        : well.jobType.includes("PSR")
        ? "#FF0000"
        : "#FF0000";

    if (well.markerType === "cartofactWell") {
        color = "#CCCCCC";
    }
    if (well.bulkSelected) {
        color = "#FFA500";
    }

    return color;
};


export function  calculateNewCoordsWithOffsets(startLat, startLng, data, stageLength, maxStages, wellName) {
  const R = 6371e3; // Earth radius in meters
  let new_data = [{
    wellName: wellName,
    lat: startLat,
    lng: startLng,
    depth: 0,
    azimuth: 0,
    inclination: 0,
    source: 0,
    stage: wellName,
  }];
      
  // To keep track of which stages have already been added
  let stagesEncountered = new Set();

  // Find the maximum TVD to define stage divisions from the bottom up
  let maxTVD = Math.max(...data.map(entry => entry.TVD));

  data.forEach(entry => {
    if (entry.Survey === "Tie In") {
      // Skip the first entry
      return;
    }

    let deltaNS = entry.NS / R;
    let deltaEW = entry.EW / (R * Math.cos(Math.PI * startLat / 180));
    let newLat = startLat + deltaNS * 180 / Math.PI;
    let newLng = startLng + deltaEW * 180 / Math.PI;

    // Calculate stage number
    let stage = Math.ceil((maxTVD - entry.TVD) / stageLength);
    //console.log("directionaSurvyer.stage", stage, "depth", entry.TVD);
    // Check if the stage has already been encountered and does not exceed maxStages
    if (!stagesEncountered.has(stage) && stage <= maxStages) {
      let newEntry = {
        wellName: wellName,
        lat: newLat,
        lng: newLng,
        depth: entry.TVD,
        azimuth: entry.Azimuth,
        inclination: entry.Inc,
        source: entry.Source,
        stage: stage,
      };
      new_data.push(newEntry);
      stagesEncountered.add(stage);
    }
  });

  return new_data;
}

export function findHeelPoint(data) {
  const heelInclineThreshold = 80; // degrees
    
  for (let i = 0; i < data?.length; i++) {
    const point = data[i];
    const inclination = point.Inc ?? point.inc ?? point.inclination ?? point.Inclination;      
    if (inclination >= heelInclineThreshold) {
      return point;
    }
  }

  // no heel found maybe return last point
  return null; // or return data[data.length - 1];
}


// Create stages based off data and number of stages
export function createStages(data, maxStages) {
  let heelIndex = data.findIndex(point => point === findHeelPoint(data));
  if (heelIndex === -1) {
    console.error("Heel point not found.");
    return [];
  }

  let toeIndex = data.length - 1;
  let totalPoints = toeIndex - heelIndex;
  let pointsPerStage = Math.floor(totalPoints / maxStages);
  let remainingPoints = totalPoints % maxStages;

  let stages = [];
  for (let i = 0; i <= maxStages; i++) {
    let extraPoint = i < remainingPoints ? 1 : 0;
    let stageStartIndex = heelIndex + i * pointsPerStage + Math.min(i, remainingPoints);
    let stageEndIndex = stageStartIndex + pointsPerStage + extraPoint;

    let midIndex = Math.floor((stageStartIndex + stageEndIndex) / 2);
    midIndex = Math.min(Math.max(midIndex, heelIndex), toeIndex); // Ensure within bounds

    //console.log({stage: maxStages - i });
    stages.push({ ...data[midIndex], stage: maxStages - i });
  }

  return stages;
}

export function calculateCoordinates(startLat, startLng, point, R) {
  // Convert NS and EW offset (in meters) to geocoordinates
  let deltaNS = point.NS / R;
  let deltaEW = point.EW / (R * Math.cos(Math.PI * startLat / 180));

  let newLat = startLat + deltaNS * 180 / Math.PI;
  let newLng = startLng + deltaEW * 180 / Math.PI;

  return {
    wellName: 'wellName', // overwritten in caller
    lat: newLat,
    lng: newLng,
    depth: point.TVD,
    azimuth: point.Azimuth,
    inclination: point.Inc,
    source: point.Source,
    stage: point.stage,
  };
}


export function processDirectionalSurveyData(startLat, startLng, data, maxStages, wellName, wellId = null) {
  const heelPoint = findHeelPoint(data);
  console.log('heelPoint', heelPoint);
  const toePoint = data[data.length - 1];
  console.log('toePoint', toePoint);
  const stages = createStages(data, maxStages);
  console.log(stages);
  const R = 6371e3; // Earth radius in meters

  // Process Surface to Heel
  let surfaceData = calculateCoordinates(startLat, startLng, data[0], R);
  surfaceData.wellName = wellName;
  surfaceData.stage = wellName;
  let heelData = calculateCoordinates(startLat, startLng, heelPoint, R);
  heelData.stage = null;

  // Process Heel to Toe
  let heelToToeData = stages.map(stage => {
      return calculateCoordinates(startLat, startLng, stage, R);
  });

  return [
    surfaceData,
    heelData,
    ...heelToToeData
  ];
}


export function generateDirectionalSurveyFromSurfaceAndBottomHole({
  surfaceLat,
  surfaceLng,
  bottomHoleLat,
  bottomHoleLng,
  totalDepth,
  heelDepth = 500, // default to same as totalDepth
  steps = 20, // total number of steps for interpolation
  wellName = 'Well',
}) {
  const R = 6371e3; // Earth radius in meters

  // Step 1: Vertical segment (surface to heel)
  const verticalSteps = Math.floor(steps * (heelDepth / totalDepth));
  const verticalSegment = Array.from({ length: verticalSteps }, (_, i) => ({
    wellName,
    lat: surfaceLat,
    lng: surfaceLng,
    depth: (heelDepth / verticalSteps) * i,
    azimuth: 0,
    inclination: 0,
    stage: null,
  }));

  // Step 2: Horizontal segment (heel to toe)
  const remainingSteps = steps - verticalSteps;
  const deltaLat = bottomHoleLat - surfaceLat;
  const deltaLng = bottomHoleLng - surfaceLng;

  const horizontalSegment = Array.from({ length: remainingSteps + 1 }, (_, i) => ({
    wellName,
    lat: surfaceLat + (deltaLat * i) / remainingSteps,
    lng: surfaceLng + (deltaLng * i) / remainingSteps,
    depth: heelDepth + ((totalDepth - heelDepth) * i) / remainingSteps,
    azimuth: 90, // generic
    inclination: 90, // assumed horizontal
    stage: null,
  }));

  return [...verticalSegment, ...horizontalSegment];
}

export function generateSimpleDirectionalSurvey({
  surfaceLat,
  surfaceLng,
  bottomHoleLat,
  bottomHoleLng,
  totalDepth,
  heelDepth = 500, // default pass same value as totalDepth
  well = null
}) {
  return {
    coordinates: [
      {
        well: { unique_identifier: well?.unique_identifier || 'Unknown Well' },
        lat: surfaceLat,
        lng: surfaceLng,
        depth: 0.0,
        azimuth: 0.0,
        inclination: 0.0,
      },
      {
        well: { unique_identifier: well?.unique_identifier || 'Unknown Well' },
        lat: surfaceLat,
        lng: surfaceLng,
        depth: heelDepth,
        azimuth: 0.0,
        inclination: 81.0,
      },
      {
        well: { unique_identifier: well?.unique_identifier || 'Unknown Well' },
        lat: bottomHoleLat,
        lng: bottomHoleLng,
        depth: totalDepth,
        azimuth: 90.0,
        inclination: 90.0,
      },
    ],
    heel: {
      well: { unique_identifier: well?.unique_identifier || 'Unknown Well' },
      lat: surfaceLat,
      lng: surfaceLng,
      depth: heelDepth,
      azimuth: 0.0,
      inclination: 81.0,
    },
    toe: {
      well: { unique_identifier: well?.unique_identifier || 'Unknown Well' },
      lat: bottomHoleLat,
      lng: bottomHoleLng,
      depth: totalDepth,
      azimuth: 90.0,
      inclination: 90.0,
    },
  };
}

export function getSurveysIntersectingSphere(surveys, center, radiusMeters) {
  // Convert geographic lat/lng/depth to 3D Cartesian coordinates
  const toXYZ = ({ lat, lng, depth }) => {
    const R = 6371000 - depth; // Approx Earth radius in meters minus depth
    const phi = (lat * Math.PI) / 180;
    const theta = (lng * Math.PI) / 180;
    return {
      x: R * Math.cos(phi) * Math.cos(theta),
      y: R * Math.cos(phi) * Math.sin(theta),
      z: R * Math.sin(phi),
    };
  };

  const centerXYZ = toXYZ(center); // Convert sphere center to 3D

  // Filter surveys that have at least one point within the sphere radius
  const intersectingSurveys = surveys.filter(survey =>
    survey.coordinates.some(point => {
      const p = toXYZ(point);
      const dx = p.x - centerXYZ.x;
      const dy = p.y - centerXYZ.y;
      const dz = p.z - centerXYZ.z;
      return Math.sqrt(dx * dx + dy * dy + dz * dz) <= radiusMeters;
    })
  );

  // Return the intersecting surveys and a 2D display object (Google Maps Circle)
  return {
    intersectingSurveys,
    displayObject: {
      type: 'circle',
      center: { lat: center.lat, lng: center.lng },
      radius: radiusMeters
    }
  };
}


export function getSurveysIntersectingCylinderDELETE(surveys, point1, point2, radiusMeters) {
  // Convert lat/lng/depth to 3D Cartesian coordinates
  const toVec = ({ lat, lng, depth }) => {
    const R = 6371000 - depth;
    const phi = (lat * Math.PI) / 180;
    const theta = (lng * Math.PI) / 180;
    return [
      R * Math.cos(phi) * Math.cos(theta),
      R * Math.cos(phi) * Math.sin(theta),
      R * Math.sin(phi),
    ];
  };

  const A = toVec(point1); // Cylinder base
  const B = toVec(point2); // Cylinder top
  const AB = B.map((val, i) => val - A[i]); // Vector from A to B
  const AB_lenSq = AB.reduce((sum, val) => sum + val * val, 0); // |AB|²

  // Filter surveys with at least one point inside the cylinder
  const intersectingSurveys = surveys.filter(survey =>
    survey.coordinates.some(p => {
      const P = toVec(p); // Point on survey
      const AP = P.map((val, i) => val - A[i]);
      const t = AB.reduce((sum, val, i) => sum + val * AP[i], 0) / AB_lenSq; // Projection scalar

      if (t < 0 || t > 1) return false; // Outside cylinder height

      const proj = A.map((val, i) => val + t * AB[i]); // Closest point on axis
      const distSq = proj.reduce((sum, val, i) => sum + (P[i] - val) ** 2, 0); // Distance to axis

      return distSq <= radiusMeters * radiusMeters;
    })
  );

  // Return results and 2D cylinder representation for display
  return {
    intersectingSurveys,
    displayObject: {
      type: 'cylinder',
      path: [
        { lat: point1.lat, lng: point1.lng },
        { lat: point2.lat, lng: point2.lng }
      ],
      radius: radiusMeters
    }
  };
}

export function getSurveysIntersectingCylinder(surveys, selected, radiusMeters) {
  const point1 = selected.polylinePath[1];
  const point2 = selected.polylinePath[selected.polylinePath.length - 1];
  const toVec = ({ lat, lng, depth }) => {
    const R = 6371000 - depth;
    const phi = (lat * Math.PI) / 180;
    const theta = (lng * Math.PI) / 180;
    return [
      R * Math.cos(phi) * Math.cos(theta),
      R * Math.cos(phi) * Math.sin(theta),
      R * Math.sin(phi),
    ];
  };

  const A = toVec(point1);
  const B = toVec(point2);
  const AB = B.map((val, i) => val - A[i]);
  const AB_lenSq = AB.reduce((sum, val) => sum + val * val, 0);
  const radiusSq = radiusMeters * radiusMeters;

  const intersectingSurveys = surveys.filter(survey =>
    Array.isArray(survey.coordinates) &&
    survey.coordinates.some(p => {
      const P = toVec(p);
      const AP = P.map((val, i) => val - A[i]);
      const t = AB.reduce((sum, val, i) => sum + val * AP[i], 0) / AB_lenSq;

      // Case 1: within main cylinder
      if (t >= 0 && t <= 1) {
        const proj = A.map((val, i) => val + t * AB[i]);
        const distSq = proj.reduce((sum, val, i) => sum + (P[i] - val) ** 2, 0);
        if (distSq <= radiusSq) return true;
      }

      // Case 2: within spherical cap at point1
      const distToA = A.reduce((sum, val, i) => sum + (P[i] - val) ** 2, 0);
      if (distToA <= radiusSq) return true;

      // Case 3: within spherical cap at point2
      const distToB = B.reduce((sum, val, i) => sum + (P[i] - val) ** 2, 0);
      if (distToB <= radiusSq) return true;

      return false;
    })
  );

  return {
    intersectingSurveys,
    displayObject: {
      type: 'cylinder',
      path: [
        { lat: point1.lat, lng: point1.lng },
        { lat: point2.lat, lng: point2.lng }
      ],
      radius: radiusMeters
    }
  };
}


export function metersToDegrees(meters) {
  const R = 6371000; // Earth radius in meters
  return (meters * 180) / (Math.PI * R);

  //or
  //return meters / 111320; // shortcut
}


// Categorize well status 
export function categorizeStatus(rawStatus) {
  const status = rawStatus?.toUpperCase();
  if (!status) return "unknown";
  
  if (
    status.includes("ABANDONED") || status.includes("ABNZ") ||
    status.includes("JUNK") || status.includes("PLUGGED")
  ) {
    return "abandoned";
  }

  if (
    status.includes("SUSPENDED") || status.includes("SHUT") ||
    status.includes("STANDING") || status.includes("DORMANT") ||
    status.includes("IDLE")
  ) {
    return "suspended";
  }

  if (
    status.includes("ACTIVE") || status.includes("PRODUCING") ||
    status.includes("FLOWING") || status.includes("PUMPING") ||
    status.includes("COMPLETED") || status.includes("GAS WELL") ||
    status.includes("OIL WELL")
  ) {
    return "active";
  }

  if (status.includes("CASED")) {
    return "cased";
  }

  return "unknown";
}

export function calculateRisk({
  distanceM, // Horizontal distance from frac (in meters)
  verticalDiffM, // Vertical difference in TVD from frac zone (in meters)
  statusText, // 'active', 'suspended', 'abandoned', 'cased', 'open-hole'
  sharedZone = false,  // boolean same target formation (zone)
  barrierSystem = 'unknown' // 'single', 'double', 'unknown' how many casing barriers exist between the frac interval and the surface
}) {
  const status = categorizeStatus(statusText);

  const makeResult = (risk) => {
    const colorMap = {
      High: 'lightcoral',
      Caution: 'lightyellow',
      Low: 'lightgreen'
    };
    return { risk, color: colorMap[risk] };
  };  

  if (distanceM > 1125) {
    return makeResult('Low');
  }

  if (distanceM < 400) {
    if (status === 'abandoned') return makeResult('Low');
    return makeResult('High');
  }

  if (distanceM > 750 && distanceM <= 1125) {
    if (status === 'abandoned') return makeResult('Low');
    //if (sharedZone) return 'Caution';
    return makeResult('Low');
  }

  if (distanceM <= 750) {
    if (status === 'abandoned' && verticalDiffM > 200) return makeResult('Caution');
    //if (sharedZone && verticalDiffM < 50) return 'High';
    if (status === 'suspended' && verticalDiffM < 100) return makeResult('Caution');
    //if (barrierSystem === 'single') return 'High';
    return makeResult('Caution');
  }

  return makeResult('Low');
}

// function to add a new object to an array if it doesn't already exist based on a unique key
export function addUniqueByKey(array, newItem, key = 'unique_identifier') {
  const exists = array.some((item) => item[key] === newItem[key]);
  return exists ? array : [...array, newItem];
}


/**
  Change the hex color code to rgb
  using chroma-js library.
 */
  export function hexToRgb(color) {
    return chroma(color).rgb().join(", ");
  }
  /**
    Uuses the hexToRgb() function
    to convert the hex code into rgb for using it inside the rgba color format.
   */
  export function rgba(color, opacity) {
    return `rgba(${hexToRgb(color)}, ${opacity})`;
  }
  

  




//
// Map functions
//


const cleanPoint = ({ lat, lng }) => ({ lat, lng });

const toLatLngLiteral = (latLng) => ({
  lat: latLng.lat(),
  lng: latLng.lng(),
});


export function generateFpzPolygon(marker, radiusMeters = 500, arcSteps = 16) {
  // console.log('generateFpzPolygon', point1, point2, radiusMeters);
  if (!marker || !marker.heel || !marker.toe) return null;
  
  const point1 = cleanPoint(marker.heel);
  const point2 = cleanPoint(marker.toe);
  const heading = computeHeading(point1, point2);
  const path = [];

  // Arc at point1 (start cap)
  for (let i = 0; i <= arcSteps; i++) {
    const angle = heading + 90 + (180 * i) / arcSteps;
    const pt = computeOffset(point1, radiusMeters, angle);
    path.push(toLatLngLiteral(pt));
  }

  // Arc at point2 (end cap)
  for (let i = 0; i <= arcSteps; i++) {
    const angle = heading - 90 + (180 * i) / arcSteps;
    const pt = computeOffset(point2, radiusMeters, angle);
    path.push(toLatLngLiteral(pt));
  }

  // Close polygon
  path.push(path[0]);

  // get bounding box
  const lats = path.map(p => p.lat);
  const lngs = path.map(p => p.lng);

  const envelope = {
    lat_min: Math.min(...lats),
    lat_max: Math.max(...lats),
    lng_min: Math.min(...lngs),
    lng_max: Math.max(...lngs)
  };

  // console.log('path', path, 'boundingBox', envelope);
  
  return { path, boundingBox: envelope };
}


// IN DEV
export function generateFpzPolygonFromPath(polyline, radiusMeters, arcSteps = 8) {
  if (!polyline || polyline.length < 2) return [];

  const leftSide = [];
  const rightSide = [];

  for (let i = 0; i < polyline.length - 1; i++) {
    const p1 = cleanPoint(polyline[i]);
    const p2 = cleanPoint(polyline[i + 1]);
    const heading = computeHeading(p1, p2);

    // Offset left and right from point1
    leftSide.push(toLatLngLiteral(computeOffset(p1, radiusMeters, heading - 90)));
    rightSide.push(toLatLngLiteral(computeOffset(p1, radiusMeters, heading + 90)));

    // If it's the last segment, also offset point2
    if (i === polyline.length - 2) {
      leftSide.push(toLatLngLiteral(computeOffset(p2, radiusMeters, heading - 90)));
      rightSide.push(toLatLngLiteral(computeOffset(p2, radiusMeters, heading + 90)));
    }
  }

  // Optionally add semicircle caps at each end
  const path = [];

  // Start cap
  const start = cleanPoint(polyline[0]);
  const startHeading = computeHeading(polyline[0], polyline[1]);
  for (let i = 0; i <= arcSteps; i++) {
    const angle = startHeading + 90 - (180 * i) / arcSteps;
    path.push(toLatLngLiteral(computeOffset(start, radiusMeters, angle)));
  }

  // Add left side
  path.push(...leftSide.slice(1, -1)); // exclude duplicates

  // End cap
  const end = cleanPoint(polyline[polyline.length - 1]);
  const endHeading = computeHeading(polyline[polyline.length - 2], polyline[polyline.length - 1]);
  for (let i = 0; i <= arcSteps; i++) {
    const angle = endHeading - 90 + (180 * i) / arcSteps;
    path.push(toLatLngLiteral(computeOffset(end, radiusMeters, angle)));
  }

  // Add right side in reverse
  path.push(...rightSide.slice(1, -1).reverse());

  path.push(path[0]); // close polygon

  // get bounding box
  const lats = path.map(p => p.lat);
  const lngs = path.map(p => p.lng);

  const envelope = {
    lat_min: Math.min(...lats),
    lat_max: Math.max(...lats),
    lng_min: Math.min(...lngs),
    lng_max: Math.max(...lngs)
  };

  console.log('generateFpzPolygonFromPath: path', path, 'boundingBox', envelope);
  return { path, boundingBox: envelope };
}


// BEARING degree to text
function bearingToCompass(bearing) {
  const directions = [
    'North', 'North-Northeast', 'Northeast', 'East-Northeast',
    'East', 'East-Southeast', 'Southeast', 'South-Southeast',
    'South', 'South-Southwest', 'Southwest', 'West-Southwest',
    'West', 'West-Northwest', 'Northwest', 'North-Northwest'
  ];
  const index = Math.round(bearing / 22.5) % 16;
  return directions[index];
}

// CALCULATE bearings and parallelism for offset wells to treatment wells (also treatment wells to treatment wells)
export function calculateBearingAndParallel(wellA, wellB) {
  const toRad = deg => (deg * Math.PI) / 180;
  const toDeg = rad => (rad * 180) / Math.PI;

  const getBearing = (lat1, lon1, lat2, lon2) => {
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lon2 - lon1);

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) -
              Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

    let θ = Math.atan2(y, x);
    return (toDeg(θ) + 360) % 360;
  };

  if (
    wellA.lat == null || wellA.lng == null ||
    wellA.bottomHoleLat == null || wellA.bottomHoleLng == null ||
    wellB.lat == null || wellB.lng == null ||
    wellB.bottomHoleLat == null || wellB.bottomHoleLng == null
  ) {
    return { bearingA: null, bearingB: null, isParallel: false, isSameBearing: false };
  }

  const bearingA = getBearing(wellA.lat, wellA.lng, wellA.bottomHoleLat, wellA.bottomHoleLng);
  const bearingB = getBearing(wellB.lat, wellB.lng, wellB.bottomHoleLat, wellB.bottomHoleLng);

  const angleDiff = Math.abs(bearingA - bearingB);
  const isSameBearing = angleDiff < 5;  // Same direction (e.g., both heading NE)
  const isParallel = angleDiff < 10 || Math.abs(angleDiff - 180) < 10;  // Either same or opposite direction

  return { bearingA, bearingB, isParallel, isSameBearing };
}


// SUMMARIZE well information for report
export function generateWellSummary(well, treatmentWell, calculateRisk) {
  const {
    uwi,
    type,
    tvd,
    status,
    distance,
    verticalDiff,
    sharedZone,
    barrierSystem,
  } = well;
  const allWells = treatmentWell?.offsetWells || [];
  const { isSameBearing, isParallel, bearingA, bearingB } = calculateBearingAndParallel(treatmentWell, well);

  const risk = calculateRisk({
    distanceM: distance,
    verticalDiffM: verticalDiff,
    statusText: status,
    sharedZone,
    barrierSystem
  });

  let summary = `This is a ${type} well`;
  if (status) summary += ` with status '${status}'`;
  if (tvd) summary += ` and a total vertical depth of ${Math.round(tvd)} meters`;
  summary += ". ";

  // If offset, describe its relationship to the treatment well
  if (type.toLowerCase() === "offset") {
    summary += `It is located approximately ${Math.abs(distance).toFixed(2)} meters from treatment well ${treatmentWell.unique_identifier}`;
    if (verticalDiff !== null && verticalDiff !== undefined) {
      summary += ` with a vertical separation of ${Math.abs(verticalDiff).toFixed(2)} meters`;
    }
    if (bearingB) {
      summary += ` towards the ${bearingToCompass(bearingB)}`;
      if (isSameBearing) {
        summary += " and both wells share a similar trajectory";
      }
    }
    summary += ". ";

    if (isParallel) summary += "The wellbores appear to run in parallel. ";
    // if (sharedZone) summary += "It shares the same zone as the treatment well. ";
    // if (barrierSystem) summary += `Barrier system is '${barrierSystem}'. `;

    summary += `Overall risk is rated as ${risk.risk}.`;
  }

  // If treatment, mention nearby offsets
  if (type === "treatment") {
    const nearbyOffsets = allWells.filter(
      (w) => w.type === "offset" && w.related_to === uwi && w.distance <= 1125
    );

    if (nearbyOffsets.length === 0) {
      summary += `No offset wells were found within 1125 meters.`;
    } else {
      summary += `${nearbyOffsets.length} offset well(s) lie within 1125 meters. `;

      const highRiskOffsets = nearbyOffsets.filter((w) => {
        const r = calculateRisk({
          distanceM: w.distance,
          verticalDiffM: w.verticalDiff,
          statusText: w.status,
          sharedZone: w.sharedZone,
          barrierSystem: w.barrierSystem
        });
        return r.risk === "High";
      });

      if (highRiskOffsets.length > 0) {
        summary += `${highRiskOffsets.length} of these are rated High risk.`;
      }
    }
  }

  return summary;
}

export function padBboxDegrees(bbox, padMeters) {
  // bbox: { lat_min, lat_max, lng_min, lng_max }
  const latCenter = (bbox.lat_min + bbox.lat_max) / 2;

  const metersPerDegLat = 111_320; // approx
  const metersPerDegLng = 111_320 * Math.cos((latCenter * Math.PI) / 180);

  const dLat = padMeters / metersPerDegLat;
  const dLng = padMeters / metersPerDegLng;

  const lat_min = Math.max(-90, bbox.lat_min - dLat);
  const lat_max = Math.min( 90, bbox.lat_max + dLat);
  const lng_min = Math.max(-180, bbox.lng_min - dLng);
  const lng_max = Math.min( 180, bbox.lng_max + dLng);

  return { lat_min, lat_max, lng_min, lng_max };
}